/**
 * AdminPlansTab - CRUD for subscription plans.
 *
 * Table UI for managing plans with pricing, limits, and configuration:
 * - Name, slug, period checkboxes with conditional price fields
 * - Carousel/generation/export limits with unlimited toggles
 * - Features list, highlight color, sort order, active toggle
 * - Create new plans and edit existing ones inline
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Plus, Pencil, Save, X, Trash2 } from 'lucide-react';
import {
  getPlans,
  createPlan,
  updatePlan,
  togglePlan,
  deletePlan,
  type PlanData,
} from '@/services/adminService';

const formatBRL = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

interface PlanFormState {
  name: string;
  slug: string;
  monthlyEnabled: boolean;
  quarterlyEnabled: boolean;
  yearlyEnabled: boolean;
  priceMonthly: number;
  priceQuarterly: number;
  priceYearly: number;
  carouselUnlimited: boolean;
  generationUnlimited: boolean;
  exportUnlimited: boolean;
  carouselLimit: number;
  generationLimit: number;
  exportLimit: number;
  features: string;
  highlightColor: string;
  sortOrder: number;
  isPublic: boolean;
  checkoutUrl: string;
  onprofitId: string;
}

const EMPTY_FORM: PlanFormState = {
  name: '',
  slug: '',
  monthlyEnabled: true,
  quarterlyEnabled: false,
  yearlyEnabled: false,
  priceMonthly: 0,
  priceQuarterly: 0,
  priceYearly: 0,
  carouselUnlimited: true,
  generationUnlimited: true,
  exportUnlimited: true,
  carouselLimit: 10,
  generationLimit: 10,
  exportLimit: 10,
  features: '',
  highlightColor: '',
  sortOrder: 0,
  isPublic: true,
  checkoutUrl: '',
  onprofitId: '',
};

function planToForm(plan: PlanData): PlanFormState {
  return {
    name: plan.name,
    slug: plan.slug,
    monthlyEnabled: plan.priceMonthly > 0,
    quarterlyEnabled: plan.priceQuarterly > 0,
    yearlyEnabled: plan.priceYearly > 0,
    priceMonthly: plan.priceMonthly,
    priceQuarterly: plan.priceQuarterly,
    priceYearly: plan.priceYearly,
    carouselUnlimited: plan.carouselLimit === -1,
    generationUnlimited: plan.generationLimit === -1,
    exportUnlimited: plan.exportLimit === -1,
    carouselLimit: plan.carouselLimit === -1 ? 10 : plan.carouselLimit,
    generationLimit: plan.generationLimit === -1 ? 10 : plan.generationLimit,
    exportLimit: plan.exportLimit === -1 ? 10 : plan.exportLimit,
    features: (plan.features || []).join('\n'),
    highlightColor: plan.highlightColor || '',
    sortOrder: plan.sortOrder,
    isPublic: plan.isPublic,
    checkoutUrl: plan.checkoutUrl || '',
    onprofitId: plan.externalIdOnprofit || '',
  };
}

function getReferencePriceLabel(plan: PlanData): string {
  if (plan.priceMonthly > 0) return `${formatBRL(plan.priceMonthly)}/mês`;
  if (plan.priceQuarterly > 0) return `${formatBRL(plan.priceQuarterly)}/trim.`;
  if (plan.priceYearly > 0) return `${formatBRL(plan.priceYearly)}/ano`;
  return '-';
}

function formatLimitLabel(value: number): string {
  return value === -1 ? 'Ilimitado' : `${value}/mês`;
}

const AdminPlansTab: React.FC = () => {
  const [plans, setPlans] = useState<PlanData[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<PlanFormState>(EMPTY_FORM);
  const [isCreating, setIsCreating] = useState(false);
  const [createForm, setCreateForm] = useState<PlanFormState>(EMPTY_FORM);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const fetchPlans = useCallback(async () => {
    try {
      const { plans } = await getPlans();
      setPlans(plans);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const parseFeatures = (text: string): string[] =>
    text.split('\n').map((s) => s.trim()).filter(Boolean);

  const formToPayload = (form: PlanFormState) => ({
    name: form.name,
    slug: form.slug,
    priceMonthly: form.monthlyEnabled ? form.priceMonthly : 0,
    priceQuarterly: form.quarterlyEnabled ? form.priceQuarterly : 0,
    priceYearly: form.yearlyEnabled ? form.priceYearly : 0,
    carouselLimit: form.carouselUnlimited ? -1 : form.carouselLimit,
    generationLimit: form.generationUnlimited ? -1 : form.generationLimit,
    exportLimit: form.exportUnlimited ? -1 : form.exportLimit,
    accessDurationDays: null,
    features: parseFeatures(form.features),
    highlightColor: form.highlightColor || null,
    sortOrder: form.sortOrder,
    isPublic: form.isPublic,
    checkoutUrl: form.checkoutUrl || null,
    externalIdOnprofit: form.onprofitId || null,
  });

  const handleCreate = async () => {
    setError('');
    if (!createForm.monthlyEnabled && !createForm.quarterlyEnabled && !createForm.yearlyEnabled) {
      setError('Selecione ao menos uma periodicidade.');
      return;
    }
    try {
      await createPlan(formToPayload(createForm));
      setIsCreating(false);
      setCreateForm(EMPTY_FORM);
      fetchPlans();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleStartEdit = (plan: PlanData) => {
    setEditingId(plan.id);
    setEditForm(planToForm(plan));
  };

  const handleSaveEdit = async () => {
    if (!editingId) return;
    setError('');
    if (!editForm.monthlyEnabled && !editForm.quarterlyEnabled && !editForm.yearlyEnabled) {
      setError('Selecione ao menos uma periodicidade.');
      return;
    }
    try {
      await updatePlan({ id: editingId, ...formToPayload(editForm) });
      setEditingId(null);
      fetchPlans();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleDelete = async (planId: string) => {
    setError('');
    try {
      await deletePlan(planId);
      setDeletingId(null);
      fetchPlans();
    } catch (err: any) {
      setError(err.message);
      setDeletingId(null);
    }
  };

  const handleToggle = async (plan: PlanData) => {
    try {
      await togglePlan(plan.id, !plan.isActive);
      fetchPlans();
    } catch (err: any) {
      setError(err.message);
    }
  };

  if (loading) return <p className="text-muted-foreground">Carregando planos...</p>;

  const renderLimitRow = (
    label: string,
    isUnlimited: boolean,
    limitValue: number,
    onToggleUnlimited: (val: boolean) => void,
    onChangeLimit: (val: number) => void,
  ) => (
    <div className="flex items-center gap-4">
      <span className="text-sm text-foreground w-44 shrink-0">{label}</span>
      <div className="flex items-center gap-2">
        <Switch checked={isUnlimited} onCheckedChange={onToggleUnlimited} />
        <span className="text-xs text-muted-foreground w-16">
          {isUnlimited ? 'Ilimitado' : 'Limitado'}
        </span>
      </div>
      {!isUnlimited && (
        <Input
          type="number"
          min={1}
          value={limitValue}
          onChange={(e) => onChangeLimit(Math.max(1, parseInt(e.target.value) || 1))}
          className="w-24"
        />
      )}
    </div>
  );

  const renderForm = (
    form: PlanFormState,
    setForm: (f: PlanFormState) => void,
    onSave: () => void,
    onCancel: () => void,
    saveLabel: string,
  ) => (
    <div className="border border-border rounded-lg p-5 space-y-5 bg-muted/30">
      {/* 1. Identificação */}
      <div>
        <p className="text-sm font-semibold text-foreground mb-2">Identificação</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Nome</label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Essential"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Slug</label>
            <Input
              value={form.slug}
              onChange={(e) => setForm({ ...form, slug: e.target.value })}
              placeholder="essential"
            />
          </div>
        </div>
      </div>

      {/* 2. Periodicidade e Preços */}
      <div>
        <p className="text-sm font-semibold text-foreground mb-2">Periodicidade e Preços</p>
        <div className="space-y-2">
          {/* Mensal */}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer w-32">
              <input
                type="checkbox"
                checked={form.monthlyEnabled}
                onChange={(e) => setForm({ ...form, monthlyEnabled: e.target.checked })}
                className="rounded border-input"
              />
              <span className="text-sm">Mensal</span>
            </label>
            {form.monthlyEnabled && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">R$</span>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  value={form.priceMonthly}
                  onChange={(e) => setForm({ ...form, priceMonthly: parseFloat(e.target.value) || 0 })}
                  className="w-32"
                />
              </div>
            )}
          </div>
          {/* Trimestral */}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer w-32">
              <input
                type="checkbox"
                checked={form.quarterlyEnabled}
                onChange={(e) => setForm({ ...form, quarterlyEnabled: e.target.checked })}
                className="rounded border-input"
              />
              <span className="text-sm">Trimestral</span>
            </label>
            {form.quarterlyEnabled && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">R$</span>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  value={form.priceQuarterly}
                  onChange={(e) => setForm({ ...form, priceQuarterly: parseFloat(e.target.value) || 0 })}
                  className="w-32"
                />
              </div>
            )}
          </div>
          {/* Anual */}
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer w-32">
              <input
                type="checkbox"
                checked={form.yearlyEnabled}
                onChange={(e) => setForm({ ...form, yearlyEnabled: e.target.checked })}
                className="rounded border-input"
              />
              <span className="text-sm">Anual</span>
            </label>
            {form.yearlyEnabled && (
              <div className="flex items-center gap-1">
                <span className="text-xs text-muted-foreground">R$</span>
                <Input
                  type="number"
                  step="0.01"
                  min={0}
                  value={form.priceYearly}
                  onChange={(e) => setForm({ ...form, priceYearly: parseFloat(e.target.value) || 0 })}
                  className="w-32"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 3. Limites de uso */}
      <div>
        <p className="text-sm font-semibold text-foreground mb-2">Limites de uso por mês</p>
        <div className="space-y-3">
          {renderLimitRow(
            'Carrosséis / mês',
            form.carouselUnlimited,
            form.carouselLimit,
            (val) => setForm({ ...form, carouselUnlimited: val }),
            (val) => setForm({ ...form, carouselLimit: val }),
          )}
          {renderLimitRow(
            'Gerações IA / mês',
            form.generationUnlimited,
            form.generationLimit,
            (val) => setForm({ ...form, generationUnlimited: val }),
            (val) => setForm({ ...form, generationLimit: val }),
          )}
          {renderLimitRow(
            'Exportações / mês',
            form.exportUnlimited,
            form.exportLimit,
            (val) => setForm({ ...form, exportUnlimited: val }),
            (val) => setForm({ ...form, exportLimit: val }),
          )}
        </div>
      </div>

      {/* 4. Features */}
      <div>
        <p className="text-sm font-semibold text-foreground mb-2">Features</p>
        <textarea
          value={form.features}
          onChange={(e) => setForm({ ...form, features: e.target.value })}
          className="w-full h-24 rounded-md border border-input bg-background px-3 py-2 text-sm"
          placeholder={"Integrações ilimitadas\nAcessos ilimitados\nSuporte prioritário"}
        />
        <p className="text-xs text-muted-foreground mt-1">Uma feature por linha</p>
      </div>

      {/* 5. Visibilidade */}
      <div>
        <p className="text-sm font-semibold text-foreground mb-2">Visibilidade</p>
        <div className="flex items-center gap-3 mb-3">
          <Switch checked={form.isPublic} onCheckedChange={(val) => setForm({ ...form, isPublic: val })} />
          <div>
            <span className="text-sm font-medium">{form.isPublic ? 'Público' : 'Interno'}</span>
            <p className="text-xs text-muted-foreground">
              {form.isPublic
                ? 'Visível na página "Meus Planos" e no paywall.'
                : 'Oculto para usuários. Apenas admins podem atribuí-lo.'}
            </p>
          </div>
        </div>
      </div>

      {/* 6. Checkout Externo */}
      <div>
        <p className="text-sm font-semibold text-foreground mb-2">Checkout Externo</p>
        <div>
          <label className="text-xs font-medium text-muted-foreground">URL de checkout (OnProfit, etc.)</label>
          <Input
            value={form.checkoutUrl}
            onChange={(e) => setForm({ ...form, checkoutUrl: e.target.value })}
            placeholder="https://pay.onprofit.com.br/..."
            className="mt-1"
          />
          <p className="text-xs text-muted-foreground mt-1">
            Se preenchido, o botão de assinar redirecionará para este link ao invés de abrir o formulário de pagamento.
          </p>
        </div>
        <div className="mt-3">
          <label className="text-xs font-medium text-muted-foreground">ID OnProfit (offer hash)</label>
          <Input
            value={form.onprofitId}
            onChange={(e) => setForm({ ...form, onprofitId: e.target.value })}
            placeholder="6FfRmp"
            className="mt-1"
          />
          <p className="text-xs text-muted-foreground mt-1">
            O offer_hash da oferta na OnProfit. Necessário para ativação automática via webhook.
          </p>
        </div>
      </div>

      {/* 7. Aparência */}
      <div>
        <p className="text-sm font-semibold text-foreground mb-2">Aparência</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-medium text-muted-foreground">Cor destaque</label>
            <div className="flex gap-2 mt-1">
              <Input
                value={form.highlightColor}
                onChange={(e) => setForm({ ...form, highlightColor: e.target.value })}
                placeholder="#4CAF50"
                className="flex-1"
              />
              {form.highlightColor && (
                <div
                  className="w-9 h-9 rounded-md border border-input shrink-0"
                  style={{ backgroundColor: form.highlightColor }}
                />
              )}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Ordem de exibição</label>
            <Input
              type="number"
              value={form.sortOrder}
              onChange={(e) => setForm({ ...form, sortOrder: parseInt(e.target.value) || 0 })}
              className="mt-1"
            />
          </div>
        </div>
      </div>

      {/* 8. Ações */}
      <div className="flex gap-2 pt-2 border-t border-border">
        <Button size="sm" onClick={onSave}>
          <Save className="h-4 w-4 mr-1" /> {saveLabel}
        </Button>
        <Button size="sm" variant="outline" onClick={onCancel}>
          <X className="h-4 w-4 mr-1" /> Cancelar
        </Button>
      </div>
    </div>
  );

  return (
    <div className="space-y-4">
      {error && (
        <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg">{error}</div>
      )}

      <div className="flex justify-between items-center">
        <h2 className="text-lg font-semibold">Planos</h2>
        <Button size="sm" onClick={() => setIsCreating(true)} disabled={isCreating}>
          <Plus className="h-4 w-4 mr-1" /> Novo Plano
        </Button>
      </div>

      {/* Create form */}
      {isCreating && renderForm(
        createForm,
        setCreateForm,
        handleCreate,
        () => { setIsCreating(false); setCreateForm(EMPTY_FORM); },
        'Criar',
      )}

      {/* Edit form (shown above table when editing) */}
      {editingId && renderForm(
        editForm,
        setEditForm,
        handleSaveEdit,
        () => setEditingId(null),
        'Salvar',
      )}

      {/* Plans table */}
      <div className="border border-border rounded-lg overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Nome</th>
              <th className="text-left px-4 py-2 font-medium">Slug</th>
              <th className="text-right px-4 py-2 font-medium">Preço (ref.)</th>
              <th className="text-center px-4 py-2 font-medium">Períodos</th>
              <th className="text-center px-4 py-2 font-medium">Limites</th>
              <th className="text-center px-4 py-2 font-medium">Cor</th>
              <th className="text-center px-4 py-2 font-medium">Ordem</th>
              <th className="text-center px-4 py-2 font-medium">Público</th>
              <th className="text-center px-4 py-2 font-medium">Checkout</th>
              <th className="text-center px-4 py-2 font-medium">Ativo</th>
              <th className="text-right px-4 py-2 font-medium">Ações</th>
            </tr>
          </thead>
          <tbody>
            {plans.map((plan) => (
              <tr
                key={plan.id}
                className={`border-t border-border ${editingId === plan.id ? 'bg-primary/5' : ''}`}
              >
                <td className="px-4 py-2 font-medium">{plan.name}</td>
                <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{plan.slug}</td>
                <td className="px-4 py-2 text-right text-xs">{getReferencePriceLabel(plan)}</td>
                <td className="px-4 py-2 text-center">
                  <div className="flex gap-1 justify-center">
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${plan.priceMonthly > 0 ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>M</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${plan.priceQuarterly > 0 ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>T</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${plan.priceYearly > 0 ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>A</span>
                  </div>
                </td>
                <td className="px-4 py-2 text-center">
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    <div>{formatLimitLabel(plan.carouselLimit)}</div>
                    <div>{formatLimitLabel(plan.generationLimit)}</div>
                    <div>{formatLimitLabel(plan.exportLimit)}</div>
                  </div>
                </td>
                <td className="px-4 py-2 text-center">
                  {plan.highlightColor ? (
                    <div
                      className="w-6 h-6 rounded-full border border-border mx-auto"
                      style={{ backgroundColor: plan.highlightColor }}
                      title={plan.highlightColor}
                    />
                  ) : (
                    <span className="text-muted-foreground">-</span>
                  )}
                </td>
                <td className="px-4 py-2 text-center text-muted-foreground">{plan.sortOrder}</td>
                <td className="px-4 py-2 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${plan.isPublic ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
                    {plan.isPublic ? 'Sim' : 'Interno'}
                  </span>
                </td>
                <td className="px-4 py-2 text-center">
                  <span className={`text-xs px-2 py-0.5 rounded font-medium ${plan.checkoutUrl ? 'bg-green-500/15 text-green-600' : 'bg-muted text-muted-foreground'}`}>
                    {plan.checkoutUrl ? 'Externo' : '-'}
                  </span>
                </td>
                <td className="px-4 py-2 text-center">
                  <Switch checked={plan.isActive} onCheckedChange={() => handleToggle(plan)} />
                </td>
                <td className="px-4 py-2 text-right">
                  {deletingId === plan.id ? (
                    <div className="flex items-center gap-1 justify-end">
                      <span className="text-xs text-destructive mr-1">Excluir?</span>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(plan.id)}
                        className="h-7 px-2 text-xs"
                      >
                        Sim
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDeletingId(null)}
                        className="h-7 px-2 text-xs"
                      >
                        Não
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-1 justify-end">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleStartEdit(plan)}
                        disabled={editingId === plan.id}
                      >
                        <Pencil className="h-3 w-3" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeletingId(plan.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminPlansTab;
